/**
 * GT-ADMIN-DBOPS-WAVE-10 — read-only API client for the
 * Admin Console DB-Ops Control Plane surface.
 *
 * Wraps the Wave 05+ Fastify endpoints under
 * `/v1/admin/data-plane/db-ops/*` with the existing
 * `apiGet` helper. Every call is GET-only — the Mega Sprint
 * explicitly forbids any mutation surface in the read-only UI
 * waves.
 *
 * Source-guard contract (mirrored from `data-plane/api.ts`):
 *   - this module only calls `apiGet`;
 *   - paths begin with `/v1/admin/data-plane/db-ops/`;
 *   - no fixture import;
 *   - no import from `services/**`;
 *   - no `fetch` / `XMLHttpRequest` / raw `axios`.
 */

import { apiGet, apiSend } from "../lib/api.js";
import type {
  AssignmentPlan,
  CreateDatabasePlan,
  DbOpsApprovalRecord,
  DbOpsApprovalRejectionReason,
  DbOpsDatabase,
  DbOpsEvidenceRefBundleType,
  DbOpsEvidenceRefRecord,
  DbOpsEvidenceRefStatus,
  DbOpsHost,
  DbOpsOverview,
  DbOpsPool,
  DbOpsReadinessReport,
  DbOpsRollbackRefRecord,
  MovePlan,
  PlanResponse,
  PostgresInstance,
  TenantDatabaseAssignment,
} from "./types.js";

interface ListEnvelope<T> {
  readonly items: ReadonlyArray<T>;
}

const BASE = "/v1/admin/data-plane/db-ops";

/**
 * Fetch the read-only Overview envelope from the API. Returns
 * the closed-shape `DbOpsOverview` with counts + per-enum
 * histograms + locked-false execution posture.
 */
export async function fetchDbOpsOverview(signal?: AbortSignal): Promise<DbOpsOverview> {
  return apiGet<DbOpsOverview>(`${BASE}/overview`, undefined, signal);
}

/* ── Wave 11 — Inventory ─────────────────────────────────────── */

export async function fetchDbOpsHosts(signal?: AbortSignal): Promise<ReadonlyArray<DbOpsHost>> {
  const res = await apiGet<ListEnvelope<DbOpsHost>>(`${BASE}/hosts`, undefined, signal);
  return res.items;
}

export async function fetchDbOpsPostgresInstances(
  signal?: AbortSignal,
): Promise<ReadonlyArray<PostgresInstance>> {
  const res = await apiGet<ListEnvelope<PostgresInstance>>(
    `${BASE}/postgres-instances`,
    undefined,
    signal,
  );
  return res.items;
}

export async function fetchDbOpsDatabases(
  signal?: AbortSignal,
): Promise<ReadonlyArray<DbOpsDatabase>> {
  const res = await apiGet<ListEnvelope<DbOpsDatabase>>(`${BASE}/databases`, undefined, signal);
  return res.items;
}

export async function fetchDbOpsPools(signal?: AbortSignal): Promise<ReadonlyArray<DbOpsPool>> {
  const res = await apiGet<ListEnvelope<DbOpsPool>>(`${BASE}/pools`, undefined, signal);
  return res.items;
}

export async function fetchDbOpsTenantAssignments(
  signal?: AbortSignal,
): Promise<ReadonlyArray<TenantDatabaseAssignment>> {
  const res = await apiGet<ListEnvelope<TenantDatabaseAssignment>>(
    `${BASE}/tenant-assignments`,
    undefined,
    signal,
  );
  return res.items;
}

/* ── Wave 12 — Readiness ─────────────────────────────────────── */

export async function fetchDbOpsReadiness(signal?: AbortSignal): Promise<DbOpsReadinessReport> {
  return apiGet<DbOpsReadinessReport>(`${BASE}/readiness`, undefined, signal);
}

/* ── Wave 16 — Plan-builder POSTs (dry-run) ──────────────────── */

export interface CreateDatabasePlanRequest {
  readonly targetHostId: string;
  readonly targetInstanceId: string;
  readonly proposedDatabaseName: string;
  readonly proposedPurpose: DbOpsDatabase["purpose"];
  readonly proposedPoolId?: string;
}

export interface AssignmentPlanRequest {
  readonly tenantId: string;
  readonly targetDatabaseId: string;
  readonly targetPoolId?: string;
}

export interface MovePlanRequest {
  readonly moveType: MovePlan["moveType"];
  readonly sourceHostId: string;
  readonly sourceInstanceId: string;
  readonly sourceDatabaseId: string;
  readonly targetHostId: string;
  readonly targetInstanceId: string;
  readonly targetDatabaseId?: string;
  readonly tenantId?: string;
}

export async function submitCreateDatabasePlan(
  body: CreateDatabasePlanRequest,
  signal?: AbortSignal,
): Promise<PlanResponse<CreateDatabasePlan>> {
  return apiSend<PlanResponse<CreateDatabasePlan>>(
    "POST",
    `${BASE}/plans/create-database`,
    body,
    signal,
  );
}

export async function submitAssignmentPlan(
  body: AssignmentPlanRequest,
  signal?: AbortSignal,
): Promise<PlanResponse<AssignmentPlan>> {
  return apiSend<PlanResponse<AssignmentPlan>>(
    "POST",
    `${BASE}/plans/assign-tenant-database`,
    body,
    signal,
  );
}

export async function submitMovePlan(
  body: MovePlanRequest,
  signal?: AbortSignal,
): Promise<PlanResponse<MovePlan>> {
  return apiSend<PlanResponse<MovePlan>>("POST", `${BASE}/plans/move-database`, body, signal);
}

/* ── Option C wave C-09 — Persisted plans + audit timeline ───── */

export type DbOpsPersistedPlan = CreateDatabasePlan | AssignmentPlan | MovePlan;

export interface DbOpsPersistedAuditEvent {
  readonly id: string;
  readonly kind: string;
  readonly planId: string;
  readonly actorRef: string;
  readonly subjectRef?: string;
  readonly reasonCode?: string;
  readonly requestId?: string;
  readonly observedAt: string;
}

export interface ListPersistedPlansQuery {
  readonly kind?: string;
  readonly status?: string;
  readonly tenantId?: string;
  readonly limit?: number;
}

function buildQueryString(q: Readonly<Record<string, unknown>>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

export async function fetchDbOpsPersistedPlans(
  query?: ListPersistedPlansQuery,
  signal?: AbortSignal,
): Promise<ReadonlyArray<DbOpsPersistedPlan>> {
  const qs = buildQueryString({ ...(query ?? {}) });
  const res = await apiGet<ListEnvelope<DbOpsPersistedPlan>>(
    `${BASE}/plans${qs}`,
    undefined,
    signal,
  );
  return res.items;
}

export async function fetchDbOpsPlanDetail(
  planId: string,
  signal?: AbortSignal,
): Promise<DbOpsPersistedPlan> {
  return apiGet<DbOpsPersistedPlan>(
    `${BASE}/plans/${encodeURIComponent(planId)}`,
    undefined,
    signal,
  );
}

export async function fetchDbOpsPlanAuditEvents(
  planId: string,
  signal?: AbortSignal,
): Promise<ReadonlyArray<DbOpsPersistedAuditEvent>> {
  const res = await apiGet<ListEnvelope<DbOpsPersistedAuditEvent>>(
    `${BASE}/plans/${encodeURIComponent(planId)}/audit-events`,
    undefined,
    signal,
  );
  return res.items;
}

/* ── GT-ADMIN-DBOPS-PHASE-3-APPROVALS-UI-FIX-01 ─────────────── */
/* Approvals — metadata_only client. No execute / apply / commit. */

export async function fetchDbOpsPlanApprovals(
  planId: string,
  signal?: AbortSignal,
): Promise<ReadonlyArray<DbOpsApprovalRecord>> {
  const res = await apiGet<ListEnvelope<DbOpsApprovalRecord>>(
    `${BASE}/plans/${encodeURIComponent(planId)}/approvals`,
    undefined,
    signal,
  );
  return res.items;
}

export interface RequestApprovalBody {
  readonly hardConfirmationRequired?: boolean;
}

export async function requestDbOpsPlanApproval(
  planId: string,
  body?: RequestApprovalBody,
  signal?: AbortSignal,
): Promise<{ readonly approval: DbOpsApprovalRecord }> {
  return apiSend<{ readonly approval: DbOpsApprovalRecord }>(
    "POST",
    `${BASE}/plans/${encodeURIComponent(planId)}/approval/request`,
    body ?? {},
    signal,
  );
}

export async function approveDbOpsApproval(
  approvalId: string,
  signal?: AbortSignal,
): Promise<{ readonly approval: DbOpsApprovalRecord }> {
  return apiSend<{ readonly approval: DbOpsApprovalRecord }>(
    "POST",
    `${BASE}/approvals/${encodeURIComponent(approvalId)}/approve`,
    {},
    signal,
  );
}

export async function rejectDbOpsApproval(
  approvalId: string,
  rejectionReasonCode: DbOpsApprovalRejectionReason,
  signal?: AbortSignal,
): Promise<{ readonly approval: DbOpsApprovalRecord }> {
  return apiSend<{ readonly approval: DbOpsApprovalRecord }>(
    "POST",
    `${BASE}/approvals/${encodeURIComponent(approvalId)}/reject`,
    { rejectionReasonCode },
    signal,
  );
}

export async function withdrawDbOpsApproval(
  approvalId: string,
  signal?: AbortSignal,
): Promise<{ readonly approval: DbOpsApprovalRecord }> {
  return apiSend<{ readonly approval: DbOpsApprovalRecord }>(
    "POST",
    `${BASE}/approvals/${encodeURIComponent(approvalId)}/withdraw`,
    {},
    signal,
  );
}

/* ── GT-ADMIN-DBOPS-PHASE-4-EVIDENCE-ROLLBACK-METADATA-UI-01 ── */
/* Evidence + rollback ref attach/list — metadata only. Shares  */
/* the approvals gate (DB_OPS_APPROVALS_ENABLED + _MODE). The   */
/* API never accepts or returns a real bundle body — only a     */
/* canonical hash, a sanitised inline summary, and closed-enum  */
/* status/type fields. No execution surface.                    */

export async function fetchDbOpsPlanEvidenceRefs(
  planId: string,
  signal?: AbortSignal,
): Promise<ReadonlyArray<DbOpsEvidenceRefRecord>> {
  const res = await apiGet<ListEnvelope<DbOpsEvidenceRefRecord>>(
    `${BASE}/plans/${encodeURIComponent(planId)}/evidence-refs`,
    undefined,
    signal,
  );
  return res.items;
}

export interface AttachEvidenceRefBody {
  readonly bundleId: string;
  readonly bundleType: DbOpsEvidenceRefBundleType;
  readonly canonicalHash: string;
  readonly status: DbOpsEvidenceRefStatus;
  readonly inlineSummary?: string;
  readonly validityWindowStartedAt?: string;
  readonly validityWindowExpiresAt?: string;
}

export async function attachDbOpsPlanEvidenceRef(
  planId: string,
  body: AttachEvidenceRefBody,
  signal?: AbortSignal,
): Promise<{ readonly evidenceRef: DbOpsEvidenceRefRecord }> {
  return apiSend<{ readonly evidenceRef: DbOpsEvidenceRefRecord }>(
    "POST",
    `${BASE}/plans/${encodeURIComponent(planId)}/evidence-refs`,
    body,
    signal,
  );
}

export async function fetchDbOpsPlanRollbackRefs(
  planId: string,
  signal?: AbortSignal,
): Promise<ReadonlyArray<DbOpsRollbackRefRecord>> {
  const res = await apiGet<ListEnvelope<DbOpsRollbackRefRecord>>(
    `${BASE}/plans/${encodeURIComponent(planId)}/rollback-refs`,
    undefined,
    signal,
  );
  return res.items;
}

export interface AttachRollbackRefBody {
  readonly present: boolean;
  readonly summary: string;
  readonly evidenceRefId?: string;
  readonly requiredBeforeExecution: boolean;
}

export async function attachDbOpsPlanRollbackRef(
  planId: string,
  body: AttachRollbackRefBody,
  signal?: AbortSignal,
): Promise<{ readonly rollbackRef: DbOpsRollbackRefRecord }> {
  return apiSend<{ readonly rollbackRef: DbOpsRollbackRefRecord }>(
    "POST",
    `${BASE}/plans/${encodeURIComponent(planId)}/rollback-refs`,
    body,
    signal,
  );
}

/* ── GT-OPS-POST-ER06B-UMBRELLA-01 — Runner job creation ────── */

/**
 * Closed-shape acceptance envelope returned by
 * `POST /v1/admin/data-plane/db-ops/plans/:planId/runner/jobs`.
 * The `mode` field discriminates the two preflight/execute paths.
 * The Admin UI uses this to confirm to the operator which mode
 * landed and to refresh the audit timeline.
 */
export interface DbOpsRunnerJobAcceptance {
  readonly accepted: true;
  readonly mode: "preflight" | "execute";
  readonly job: {
    readonly id: string;
    readonly planId: string;
    readonly planKind: string;
    readonly status: string;
    readonly mode: "preflight" | "execute";
    readonly resultStatusCode: string | null;
    readonly proposedDatabaseName: string | null;
  };
  readonly notExecutedMessage?: string;
  readonly queuedForExecutionMessage?: string;
}

export interface SubmitRunnerJobBody {
  readonly mode: "preflight" | "execute";
  readonly requestedDatabaseName: string;
  readonly approvalId: string;
}

/**
 * Queue a runner job for a plan. The API:
 *   - returns 200 + `DbOpsRunnerJobAcceptance` on success;
 *   - returns 422 with a closed refusal envelope when the request
 *     is malformed or the plan/approval is not in the right state;
 *   - returns 501 with the existing `execution_disabled` envelope
 *     when the gate is off (we never call this when the relevant
 *     capability flag is `false`).
 *
 * The Admin UI only calls this from the new RunnerJobsSection,
 * which gates the affordance on the capabilities envelope and the
 * plan's approval state.
 */
export async function submitDbOpsPlanRunnerJob(
  planId: string,
  body: SubmitRunnerJobBody,
  signal?: AbortSignal,
): Promise<DbOpsRunnerJobAcceptance> {
  return apiSend<DbOpsRunnerJobAcceptance>(
    "POST",
    `${BASE}/plans/${encodeURIComponent(planId)}/runner/jobs`,
    body,
    signal,
  );
}
